import { ReactNode, FC, useEffect } from 'react';
import { ChakraProvider, CSSReset } from "@chakra-ui/react"
import type { AppProps } from 'next/app';
import { NextComponentType, NextPage } from 'next';
import { Provider } from 'react-redux'
import { store } from 'lib/redux'
import { TypeofRoleLevel } from 'lib/types'
import withAccessHandler from 'lib/hoc/withAccessHandler';
import ErrorPage from './_error';
import { setMe } from 'domains/account';
import { Tracking } from 'tracking';
import { theme } from 'theme';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import getConfig from 'next/config';
import { E2EEProvider } from 'lib/e2ee';
import { CookiesProvider } from 'react-cookie';
import { XdStorageProvider } from 'lib/storage';

import '../styles/styles.css';

const { publicRuntimeConfig: { recaptchaSiteKey } } = getConfig();

type CustomNextComponentType = NextComponentType & {
  getLayout?: (page: ReactNode, pageProps?: any) => ReactNode;
  allowedRoles: TypeofRoleLevel[];
};

type Props = AppProps & {
  Component: CustomNextComponentType;
  store: any, // TODO: Type declaration
}

const App: FC<Props> = ({ Component, pageProps }) => {
  useEffect(() => {
    if (!pageProps?.me) return;
    store.dispatch(setMe(pageProps.me));
  }, [pageProps?.me]);

  if (pageProps?.statusCode) {
    return <ErrorPage {...pageProps} />;
  }

  const getLayout = Component.getLayout || ((page: NextPage, _?: any) => page);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ChakraProvider theme={theme}>
        <CSSReset />
        <Provider store={store}>
          <CookiesProvider>
            <XdStorageProvider>
              <E2EEProvider>
                {getLayout(<Component {...pageProps} /> as any, pageProps)}
              </E2EEProvider>
            </XdStorageProvider>
          </CookiesProvider>
        </Provider>
      </ChakraProvider>
      <Tracking />
    </GoogleReCaptchaProvider>
  )
}

export default withAccessHandler(App);
